import { Box, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import React from 'react';

import { PaymentInvoiceDetail } from '../../services/payment-services';
import { formatCurrency, formatToUSDate } from '../../utils/helper';
import { getPaymentStatusChipColor } from '../../utils/paymentHistoryHelpers';
import { useTranslation } from '../../utils/translations';
import { colors } from '../theme/theme';
import CommonStatusChip from '../ui-components/StatusChipComponent';

import { AccordionTableDetailsWrapper } from './CommonTableStyles';

const INVOICE_GRID_TEMPLATE_COLUMNS = '0.95fr 0.85fr 1.2fr 0.75fr 0.85fr 1fr';

interface PaymentInvoiceAccordionContentProps {
    invoices?: PaymentInvoiceDetail[];
    /**
     * Contract-scoped payment history renders `title` tooltips on the invoice number/transaction
     * type cells; the global payment history page does not. Kept as an opt-in flag so extracting
     * this shared block doesn't change either page's existing UI output.
     */
    withTitleTooltips?: boolean;
}

/**
 * Shared by the contract-scoped (`pages/contracts/paymentHistory.tsx`) and global
 * (`pages/payments/payment-history.tsx`) payment-history pages: renders the per-payment invoice
 * breakdown shown inside the accordion row, sorted most-recent-due-date first.
 */
const PaymentInvoiceAccordionContent: React.FC<PaymentInvoiceAccordionContentProps> = ({
    invoices: rawInvoices,
    withTitleTooltips = false,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));

    const invoices = [...(rawInvoices || [])].sort((a, b) => {
        const dateA = a.dueDate ? new Date(a.dueDate).getTime() : 0;
        const dateB = b.dueDate ? new Date(b.dueDate).getTime() : 0;
        return dateB - dateA;
    });

    if (invoices.length === 0) {
        return (
            <AccordionTableDetailsWrapper>
                <Typography variant="body2" color="textSecondary" textAlign="center" py={2}>
                    {t('contracts.paymentHistory.noInvoices') || 'No invoices associated with this payment'}
                </Typography>
            </AccordionTableDetailsWrapper>
        );
    }

    if (isMobileOrTablet) {
        return (
            <AccordionTableDetailsWrapper sx={{ backgroundColor: colors.fullWhite, padding: '12px', marginBottom: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                    <Typography variant="caption" color="textSecondary" fontWeight={700} letterSpacing={0.5}>
                        {(
                            t('payments.paymentHistory.appliedInvoicesBreakdown') || 'Applied Invoices Breakdown'
                        ).toUpperCase()}
                    </Typography>
                    <Typography variant="caption" color="text.primary" fontWeight={600}>
                        {invoices.length}{' '}
                        {invoices.length === 1
                            ? t('payments.paymentHistory.invoice') || 'Invoice'
                            : t('payments.paymentHistory.invoices') || 'Invoices'}
                    </Typography>
                </Stack>
                <Stack spacing={1}>
                    {invoices.map((invoice, idx) => (
                        <Box
                            key={idx}
                            sx={{
                                border: `1px solid ${colors.greyPreset[30]}`,
                                borderRadius: 1.5,
                                backgroundColor: colors.fullWhite,
                                p: 1.5,
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr',
                                    alignItems: 'center',
                                    columnGap: 1,
                                }}
                            >
                                <Stack direction="row" gap={0.5} alignItems="center" flexWrap="wrap">
                                    <Typography variant="body2" color="textSecondary">
                                        {t('payments.paymentHistory.invoice') || 'Invoice'}:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700}>
                                        {invoice.invoiceHeaderOId || '-'}
                                    </Typography>
                                </Stack>
                                <Stack
                                    direction="row"
                                    gap={0.5}
                                    alignItems="center"
                                    justifyContent="center"
                                    flexWrap="wrap"
                                >
                                    <Typography variant="body2" color="textSecondary">
                                        {t('contracts.paymentHistory.method') || 'Method'}:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700}>
                                        {invoice.paymentType || 'N/A'}
                                    </Typography>
                                </Stack>
                                <CommonStatusChip
                                    label={invoice.status || 'N/A'}
                                    chipBgColor={getPaymentStatusChipColor(invoice.status ?? '')}
                                    sx={{ fontSize: 11, padding: '2px 6px', justifySelf: 'end' }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr',
                                    columnGap: 1,
                                    mt: 1.5,
                                }}
                            >
                                <Stack spacing={0.25}>
                                    <Typography variant="caption" color="textSecondary">
                                        {t('contracts.paymentHistory.dueDate') || 'Due Date'}:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {invoice.dueDate ? formatToUSDate(invoice.dueDate) : 'N/A'}
                                    </Typography>
                                </Stack>
                                <Stack spacing={0.25}>
                                    <Typography variant="caption" color="textSecondary">
                                        {t('contracts.paymentHistory.transactionCode') || 'Transaction Type'}:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={600}>
                                        {invoice.transactionCode || 'N/A'}
                                    </Typography>
                                </Stack>
                                <Stack spacing={0.25} alignItems="flex-end">
                                    <Typography variant="caption" color="textSecondary">
                                        {t('contracts.paymentHistory.appliedAmount') || 'Applied Amount'}:
                                    </Typography>
                                    <Typography variant="body2" fontWeight={700}>
                                        {formatCurrency(Math.max(0, invoice.appliedAmount || 0))}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Box>
                    ))}
                </Stack>
            </AccordionTableDetailsWrapper>
        );
    }

    return (
        <AccordionTableDetailsWrapper>
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: INVOICE_GRID_TEMPLATE_COLUMNS,
                    gap: 1,
                    mb: 1.5,
                    pb: 1,
                    borderBottom: `1px solid ${colors.greyPreset[30]}`,
                }}
            >
                <Typography variant="caption" color="textSecondary" fontWeight={600}>
                    {t('contracts.paymentHistory.dueDate') || 'Due Date'}
                </Typography>
                <Typography variant="caption" color="textSecondary" fontWeight={600} textAlign="center">
                    {t('contracts.paymentHistory.invoiceId') || 'Invoice Number'}
                </Typography>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>
                    {t('contracts.paymentHistory.transactionCode') || 'Transaction Type'}
                </Typography>
                <Typography variant="caption" color="textSecondary" fontWeight={600} textAlign="center">
                    {t('contracts.paymentHistory.method') || 'Method'}
                </Typography>
                <Typography variant="caption" color="textSecondary" fontWeight={600} textAlign="center">
                    {t('contracts.paymentHistory.status') || 'Status'}
                </Typography>
                <Typography variant="caption" color="textSecondary" fontWeight={600} textAlign="right">
                    {t('contracts.paymentHistory.appliedAmount') || 'Applied Amount'}
                </Typography>
            </Box>

            <Stack spacing={1}>
                {invoices.map((invoice, idx) => (
                    <Box
                        key={idx}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: INVOICE_GRID_TEMPLATE_COLUMNS,
                            gap: 1,
                            py: 0.5,
                            borderBottom: idx < invoices.length - 1 ? `1px solid ${colors.greyPreset[20]}` : 'none',
                        }}
                    >
                        <Typography variant="body2">
                            {invoice.dueDate ? formatToUSDate(invoice.dueDate) : 'N/A'}
                        </Typography>
                        <Typography
                            variant="body2"
                            noWrap
                            title={withTitleTooltips ? invoice.invoiceHeaderOId || '-' : undefined}
                            textAlign="center"
                        >
                            {invoice.invoiceHeaderOId || '-'}
                        </Typography>
                        <Typography
                            variant="body2"
                            noWrap
                            title={withTitleTooltips ? invoice.transactionCode || 'N/A' : undefined}
                        >
                            {invoice.transactionCode || 'N/A'}
                        </Typography>
                        <Typography variant="body2" textAlign="center">
                            {invoice.paymentType || 'N/A'}
                        </Typography>
                        <Box display="flex" justifyContent="center">
                            <CommonStatusChip
                                label={invoice.status || 'N/A'}
                                chipBgColor={getPaymentStatusChipColor(invoice.status ?? '')}
                                sx={{ minWidth: 80 }}
                            />
                        </Box>
                        <Typography variant="body2" textAlign="right">
                            {formatCurrency(Math.max(0, invoice.appliedAmount || 0))}
                        </Typography>
                    </Box>
                ))}
            </Stack>
        </AccordionTableDetailsWrapper>
    );
};

export default PaymentInvoiceAccordionContent;
